/**
 * NIZAM ONE ERP — Module Activation Query Layer (Batch 4 / PG-007)
 * Reads and mutates module_definitions and company_module_settings.
 * All writes require modules.manage permission — enforced by RLS.
 */
import { supabase } from '../supabase';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ModuleDefinition {
  id: string;
  module_key: string;
  module_name_en: string;
  module_name_ar: string | null;
  category: string;
  description_en: string | null;
  description_ar: string | null;
  is_core: boolean;
  is_active: boolean;
  default_enabled: boolean;
  dependency_module_keys: string[];
  sort_order: number;
  icon_key: string | null;
}

export interface CompanyModuleSetting {
  id: string;
  company_id: string;
  module_key: string;
  is_enabled: boolean;
  enabled_at: string | null;
  enabled_by: string | null;
  disabled_at: string | null;
  disabled_by: string | null;
  reason: string | null;
  configuration_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ModuleAuditEntry {
  id: string;
  company_id: string;
  module_key: string;
  action: 'enabled' | 'disabled' | 'configured' | 'created';
  performed_by: string | null;
  actor_role: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  reason: string | null;
  created_at: string;
}

/** Merged view: definition + company setting + effective enabled state */
export interface ModuleStatus extends ModuleDefinition {
  /** Whether an explicit company_module_settings row exists */
  has_override: boolean;
  /** Final effective enabled state (company override > default_enabled) */
  effective_enabled: boolean;
  setting: CompanyModuleSetting | null;
}

// ── Read functions ─────────────────────────────────────────────────────────────

export async function getModuleDefinitions(): Promise<ModuleDefinition[]> {
  // intentionally unbounded — max 29 modules in the system catalogue; safe to fetch all
  const { data, error } = await supabase
    .from('module_definitions')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');
  if (error) throw new Error(error.message);
  return (data ?? []) as ModuleDefinition[];
}

export async function getCompanyModuleSettings(companyId: string): Promise<CompanyModuleSetting[]> {
  // intentionally unbounded — max 29 module settings per company; safe to fetch all
  const { data, error } = await supabase
    .from('company_module_settings')
    .select('*')
    .eq('company_id', companyId);
  if (error) throw new Error(error.message);
  return (data ?? []) as CompanyModuleSetting[];
}

/** Returns merged ModuleStatus array: all definitions + company overrides merged */
export async function getCompanyModuleStatuses(companyId: string): Promise<ModuleStatus[]> {
  const [defs, settings] = await Promise.all([
    getModuleDefinitions(),
    getCompanyModuleSettings(companyId),
  ]);

  const settingMap = new Map<string, CompanyModuleSetting>(
    settings.map(s => [s.module_key, s])
  );

  return defs.map(def => {
    const setting = settingMap.get(def.module_key) ?? null;
    return {
      ...def,
      has_override: setting !== null,
      effective_enabled: def.is_core
        ? true
        : setting !== null ? setting.is_enabled : def.default_enabled,
      setting,
    };
  });
}

export async function getModuleAuditLog(
  companyId: string,
  limit = 50,
): Promise<ModuleAuditEntry[]> {
  const { data, error } = await supabase
    .from('module_activation_audit_log')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as ModuleAuditEntry[];
}

// ── Mutation functions ─────────────────────────────────────────────────────────

export async function enableModule(
  companyId: string,
  moduleKey: string,
  actorProfileId: string,
  actorRole: string,
  reason?: string,
): Promise<void> {
  const now = new Date().toISOString();

  // Check if core — core modules are always enabled, skip mutation
  const { data: def } = await supabase
    .from('module_definitions')
    .select('is_core')
    .eq('module_key', moduleKey)
    .single();
  if (def?.is_core) return; // already effectively enabled

  const { data: existing } = await supabase
    .from('company_module_settings')
    .select('id, is_enabled')
    .eq('company_id', companyId)
    .eq('module_key', moduleKey)
    .maybeSingle();

  if (existing) {
    // Update
    const { error } = await supabase
      .from('company_module_settings')
      .update({ is_enabled: true, enabled_at: now, enabled_by: actorProfileId, reason, updated_at: now })
      .eq('id', existing.id);
    if (error) throw new Error(error.message);
  } else {
    // Insert
    const { error } = await supabase
      .from('company_module_settings')
      .insert({ company_id: companyId, module_key: moduleKey, is_enabled: true, enabled_at: now, enabled_by: actorProfileId, reason });
    if (error) throw new Error(error.message);
  }

  // Audit log
  await supabase.from('module_activation_audit_log').insert({
    company_id: companyId,
    module_key: moduleKey,
    action: 'enabled',
    performed_by: actorProfileId,
    actor_role: actorRole,
    old_values: existing ? { is_enabled: existing.is_enabled } : null,
    new_values: { is_enabled: true },
    reason: reason ?? null,
  });
}

export async function disableModule(
  companyId: string,
  moduleKey: string,
  actorProfileId: string,
  actorRole: string,
  reason?: string,
): Promise<void> {
  const now = new Date().toISOString();

  // Block disabling core modules
  const { data: def } = await supabase
    .from('module_definitions')
    .select('is_core')
    .eq('module_key', moduleKey)
    .single();
  if (def?.is_core) throw new Error('Core modules cannot be disabled.');

  const { data: existing } = await supabase
    .from('company_module_settings')
    .select('id, is_enabled')
    .eq('company_id', companyId)
    .eq('module_key', moduleKey)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('company_module_settings')
      .update({ is_enabled: false, disabled_at: now, disabled_by: actorProfileId, reason, updated_at: now })
      .eq('id', existing.id);
    if (error) throw new Error(error.message);
  } else {
    const { error } = await supabase
      .from('company_module_settings')
      .insert({ company_id: companyId, module_key: moduleKey, is_enabled: false, disabled_at: now, disabled_by: actorProfileId, reason });
    if (error) throw new Error(error.message);
  }

  await supabase.from('module_activation_audit_log').insert({
    company_id: companyId,
    module_key: moduleKey,
    action: 'disabled',
    performed_by: actorProfileId,
    actor_role: actorRole,
    old_values: existing ? { is_enabled: existing.is_enabled } : null,
    new_values: { is_enabled: false },
    reason: reason ?? null,
  });
}
